import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useAuth } from "@auth/AuthContext";

// LWI-1717 — the axios interceptor reads the token and tenant from
// localStorage on every request, so a tenant switch, sign-out or new sign-in
// in another tab silently re-points this tab's next request (a save could
// land in the other tenant). The `storage` event only fires in the *other*
// tabs, so when either key changes underneath us this tab is blocked until
// the user reloads it into the current session. No onClose: it can't be
// dismissed.
const WATCHED_KEYS = ["token", "tenant"];
const SETTLE_MS = 500;

const MESSAGES = {
  signedOut: {
    title: "Session ended",
    body: "Your session ended in another tab. Reload this tab to sign in again.",
  },
  tenant: {
    title: "Tenant changed in another tab",
    body: "The tenant was switched in another tab. Reload this tab to continue in the current tenant. Unsaved changes in this tab will be lost.",
  },
  session: {
    title: "Session changed in another tab",
    body: "A different user signed in from another tab. Reload this tab to continue with that session. Unsaved changes in this tab will be lost.",
  },
};

export default function CrossTabSessionDialog() {
  const { token, tenant, mode } = useAuth();
  const [reason, setReason] = useState(null);
  const authRef = useRef({ token, tenant, mode });
  authRef.current = { token, tenant, mode };

  useEffect(() => {
    let timer = null;
    const check = () => {
      timer = null;
      const current = authRef.current;
      if (!current.token) return; // not signed in here, nothing to protect
      const storedToken = localStorage.getItem("token");
      let next = null;
      if (!storedToken) next = "signedOut";
      else if (localStorage.getItem("tenant") !== current.tenant) next = "tenant";
      // An impersonation tab sends its own sessionStorage token, so only the
      // admin tab cares about a different localStorage token.
      else if (current.mode !== "impersonation" && storedToken !== current.token)
        next = "session";
      if (next) setReason(next);
    };
    const onStorage = (e) => {
      if (e.storageArea !== localStorage) return;
      // key === null means localStorage.clear().
      if (e.key !== null && !WATCHED_KEYS.includes(e.key)) return;
      // Writes arrive as separate events (switchTenant sets token, then
      // tenant), so re-check once they settle instead of judging a
      // half-written state; a transient remove+set never opens the dialog.
      clearTimeout(timer);
      timer = setTimeout(check, SETTLE_MS);
    };
    window.addEventListener("storage", onStorage);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  if (!reason) return null;
  const { title, body } = MESSAGES[reason];

  return (
    <Dialog open disableEscapeKeyDown aria-labelledby="cross-tab-session-title">
      <DialogTitle id="cross-tab-session-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{body}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </DialogActions>
    </Dialog>
  );
}
